import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

// Dynamic API URL based on current hostname
export const getApiBaseUrl = () => {
  if (typeof window === "undefined") {
    return import.meta.env.VITE_API_URL || "/api/v1";
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // External domain access (use same domain for CORS compatibility)
  if (hostname === "ione.dev-one.co.kr" || hostname === "i1.dev-one.co.kr") {
    return `${protocol}//${hostname}/api/v1`;
  }

  // Internal IP access - use Vite proxy (same host:port, let Vite proxy to backend)
  if (hostname.startsWith("192.168.")) {
    return `${protocol}//${window.location.host}/api/v1`;
  }

  // localhost - use Vite proxy
  // Vite dev server will proxy /api -> http://backend:8000
  return import.meta.env.VITE_API_URL || "/api/v1";
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  // 신뢰 디바이스 복구용 HttpOnly 쿠키(device_id)를 주고받기 위해 자격증명 포함.
  // 모든 API 출처는 same-origin이며 CORS는 명시적 화이트리스트(allow_credentials=True)라 안전.
  withCredentials: true,
});

// Token management
let accessToken: string | null = null;
// 세션 만료 통지 1회 가드 (새 토큰이 들어오면 해제)
let authExpiredNotified = false;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  if (token) {
    authExpiredNotified = false;
    localStorage.setItem("access_token", token);
  } else {
    localStorage.removeItem("access_token");
  }
};

export const getAccessToken = () => {
  if (!accessToken) {
    accessToken = localStorage.getItem("access_token");
  }
  return accessToken;
};

export const setRefreshToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("refresh_token", token);
  } else {
    localStorage.removeItem("refresh_token");
  }
};

export const getRefreshToken = () => {
  return localStorage.getItem("refresh_token");
};

export const clearTokens = () => {
  accessToken = null;
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  // Also clear auth store state to prevent redirect loop
  localStorage.removeItem("auth-storage");
};

/**
 * 진짜 인증 만료(= 로그아웃이 필요한) 오류인지 판별한다.
 * - 401/403: refresh token 만료·무효 등 진짜 인증 실패 → 로그아웃 처리해야 함
 * - 네트워크 오류(response 없음)·서버 5xx 등: 일시적 장애 → 세션 유지 (잘못된 자동 로그아웃 방지)
 *
 * 이 구분이 없으면 일시적 장애 한 번에 토큰·auth-storage가 통째로 삭제되어
 * 사용자가 멀쩡히 로그인된 상태인데도 /login 폼을 다시 보게 되는 문제가 생긴다.
 */
export const isAuthExpiredError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    // 구독 잠금/읽기 전용 403은 인증 만료가 아니다 — 세션을 지우면 잠금 화면 대신 /login으로 떨어진다
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (status === 403 && (code === "SUBSCRIPTION_LOCKED" || code === "SUBSCRIPTION_READ_ONLY")) {
      return false;
    }
    return status === 401 || status === 403;
  }
  return false;
};

/**
 * 세션 만료로 자동 로그아웃될 때 브라우저의 푸시 구독을 해제한다.
 * 웹푸시는 로그인 세션과 무관하게 브라우저로 계속 수신되므로, 여기서 끊지 않으면
 * 로그아웃된 브라우저에 알림 내용이 노출된다. (서버 발송 시점의 세션 검사와 이중 방어)
 * 토큰이 이미 무효라 서버 API는 호출하지 않는다 — 브라우저 구독만 해제하면
 * endpoint가 죽어 서버 쪽 잔여 레코드도 다음 발송 실패(404/410) 시 자동 비활성화된다.
 * (pushService를 import하면 순환 참조가 생겨 여기에 직접 구현)
 */
const unsubscribeBrowserPush = async (): Promise<void> => {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe();
  } catch {
    // best-effort: 실패해도 로그아웃 흐름을 막지 않는다
  }
};

/**
 * WebView 환경에서 네이티브 저장소의 토큰을 localStorage로 동기화
 * - 앱 재시작 시 localStorage가 클리어되어도 네이티브 저장소에서 복구
 */
export const syncTokensFromNative = async (): Promise<boolean> => {
  const isWebView = typeof window !== 'undefined' && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;

  if (!isWebView) {
    return false;
  }

  // localStorage에 이미 토큰이 있으면 동기화 불필요
  if (localStorage.getItem("access_token")) {
    return true;
  }

  console.log('[API] Syncing tokens from native storage...');

  return new Promise((resolve) => {
    const requestId = `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // 네이티브 응답 핸들러
    const handleNativeResponse = (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'TOKEN_RESPONSE' && data.requestId === requestId) {
          window.removeEventListener('message', handleNativeResponse);

          if (data.token) {
            console.log('[API] Token received from native storage');
            localStorage.setItem('access_token', data.token);
            accessToken = data.token;
            if (data.refreshToken) {
              localStorage.setItem('refresh_token', data.refreshToken);
            }
            resolve(true);
          } else {
            console.log('[API] No token in native storage');
            resolve(false);
          }
        }
      } catch {
        // JSON 파싱 실패 무시
      }
    };

    window.addEventListener('message', handleNativeResponse);

    // 네이티브에 토큰 요청
    const ReactNativeWebView = (window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } }).ReactNativeWebView;
    if (ReactNativeWebView) {
      ReactNativeWebView.postMessage(JSON.stringify({
        type: 'getToken',
        payload: { requestId }
      }));
    }

    // 3초 타임아웃
    setTimeout(() => {
      window.removeEventListener('message', handleNativeResponse);
      resolve(false);
    }, 3000);
  });
};

/**
 * 토큰을 네이티브 저장소에도 저장 (WebView에서 로그인 시)
 */
export const saveTokensToNative = (token: string, refreshToken?: string) => {
  const isWebView = typeof window !== 'undefined' && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;

  if (isWebView) {
    const ReactNativeWebView = (window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } }).ReactNativeWebView;
    if (ReactNativeWebView) {
      ReactNativeWebView.postMessage(JSON.stringify({
        type: 'saveToken',
        payload: { token, refreshToken }
      }));
    }
  }
};

/**
 * 네이티브 저장소의 토큰도 삭제
 */
export const clearNativeTokens = () => {
  const isWebView = typeof window !== 'undefined' && !!(window as unknown as { isReactNativeWebView?: boolean }).isReactNativeWebView;

  if (isWebView) {
    const ReactNativeWebView = (window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } }).ReactNativeWebView;
    if (ReactNativeWebView) {
      ReactNativeWebView.postMessage(JSON.stringify({
        type: 'clearToken',
        payload: {}
      }));
    }
  }
};

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 신뢰 기기 차단 체크를 위해 device_id 헤더 추가
    const deviceId = localStorage.getItem("ionemngr_device_id");
    if (deviceId && config.headers) {
      config.headers["X-Device-Id"] = deviceId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ─── 세션 만료 통지 ──────────────────────────────────────────────────────────
// 갱신이 진짜 인증 실패(401/403)로 끝나면 여기 한 곳에서 토큰 정리 + 푸시 해제 + 이벤트 발행.
// authStore 가 이 이벤트를 받아 스토어를 초기화하고, ProtectedRoute 가 /login 으로 보낸다.
// (api.ts → authStore 는 순환 참조라 이벤트로 느슨하게 연결. window.location 하드 이동 없음)
export const AUTH_EXPIRED_EVENT = "ionemngr:auth-expired";

export const notifyAuthExpired = (code?: string): void => {
  if (authExpiredNotified) return;
  authExpiredNotified = true;
  clearTokens();
  void unsubscribeBrowserPush();
  if (typeof window === "undefined") return;
  if (code === "SESSION_REPLACED") {
    const deviceType = (window as any).isReactNativeWebView ? "앱" : "웹";
    alert(`다른 ${deviceType}에서 로그인하여 현재 세션이 종료되었습니다.`);
  } else if (code === "DEVICE_BLOCKED" || code === "DEVICE_NOT_TRUSTED") {
    alert("이 기기는 신뢰 기기에서 삭제되었습니다. 다시 로그인해주세요.");
  } else if (code === "PASSWORD_CHANGED") {
    alert("비밀번호가 변경되었습니다. 다시 로그인해주세요.");
  }
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { code } }));
};

// JWT exp 파싱
export const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// ─── 토큰 갱신 ───────────────────────────────────────────────────────────────
// 경합 방지 3단:
//  1) 탭 안: 진행 중인 갱신 Promise 하나를 모든 호출자(인터셉터·타이머·탭 복귀·WebSocket)가 공유
//  2) 탭 간: Web Locks 로 상호배제. 락을 기다리는 동안 다른 탭이 이미 갱신했으면 그 결과를 그대로 사용
//  3) 서버: 같은 refresh token 재제출 시 같은 새 토큰 쌍을 돌려주는 멱등 캐시 (응답 유실·새로고침 대비)
type TokenPair = { access_token: string; refresh_token: string };

const REFRESH_LOCK_NAME = "ionemngr_token_refresh";
const REFRESH_TIMEOUT_MS = 20000; // 갱신 요청이 무한 대기하지 않도록 (기본 axios 인스턴스는 timeout 0)
// Web Locks 미지원 브라우저용 localStorage 폴백 락
const REFRESH_LOCK_KEY = "ionemngr_refresh_lock";
const REFRESH_LOCK_TTL = 10000;

const acquireRefreshLock = (): boolean => {
  const lock = localStorage.getItem(REFRESH_LOCK_KEY);
  if (lock && Date.now() - parseInt(lock) < REFRESH_LOCK_TTL) return false;
  localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());
  return true;
};

const releaseRefreshLock = () => {
  localStorage.removeItem(REFRESH_LOCK_KEY);
};

let pendingRefreshPromise: Promise<TokenPair | null> | null = null;

/** 갱신 호출 (탭 내 단일 Promise 공유). 저장/재스케줄/만료 처리까지 원하면 refreshSession 을 쓴다. */
export const performTokenRefresh = (): Promise<TokenPair | null> => {
  if (pendingRefreshPromise) return pendingRefreshPromise;
  pendingRefreshPromise = _doTokenRefresh().finally(() => {
    pendingRefreshPromise = null;
  });
  return pendingRefreshPromise;
};

const _postRefresh = async (refreshTokenValue: string): Promise<TokenPair> => {
  const deviceType = (window as any).isReactNativeWebView ? "app" : "web";
  const deviceId = localStorage.getItem("ionemngr_device_id");
  const response = await axios.post<TokenPair>(
    `${API_BASE_URL}/auth/refresh`,
    { refresh_token: refreshTokenValue, device_type: deviceType, device_id: deviceId },
    { timeout: REFRESH_TIMEOUT_MS },
  );
  // 락 안에서 즉시 저장 → 뒤이어 락을 잡는 다른 탭이 최신 토큰을 보고 갱신을 건너뛴다
  setAccessToken(response.data.access_token);
  setRefreshToken(response.data.refresh_token);
  return response.data;
};

const _doTokenRefresh = async (): Promise<TokenPair | null> => {
  const startRefresh = getRefreshToken();
  if (!startRefresh) return null;

  const run = async (): Promise<TokenPair | null> => {
    const latestRefresh = getRefreshToken();
    if (!latestRefresh) return null; // 대기 중 로그아웃됨
    const latestAccess = localStorage.getItem("access_token");
    if (latestRefresh !== startRefresh && latestAccess) {
      // 락을 기다리는 사이 다른 탭이 이미 갱신 완료 → 중복 회전 없이 그 결과 사용
      accessToken = latestAccess;
      authExpiredNotified = false;
      return { access_token: latestAccess, refresh_token: latestRefresh };
    }
    return _postRefresh(latestRefresh);
  };

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks && typeof locks.request === "function") {
    return locks.request(REFRESH_LOCK_NAME, run);
  }

  // 폴백: localStorage 락. 다른 탭이 갱신 중이면 토큰 교체를 최대 5초 기다린 뒤 진행
  const acquired = acquireRefreshLock();
  if (!acquired) {
    for (let i = 0; i < 10; i++) {
      await new Promise((r) => setTimeout(r, 500));
      if (getRefreshToken() !== startRefresh) break;
    }
  }
  try {
    return await run();
  } finally {
    if (acquired) releaseRefreshLock();
  }
};

/**
 * 갱신 + 저장 + 재스케줄 + 만료 처리를 한 곳에서. 갱신이 필요한 모든 경로가 이 함수만 쓴다.
 * - 진짜 인증 실패(401/403): notifyAuthExpired 로 세션 정리 후 throw
 * - 네트워크·5xx·타임아웃: 토큰을 유지한 채 throw (다음 401/타이머에서 재시도)
 */
export const refreshSession = async (): Promise<TokenPair | null> => {
  try {
    const result = await performTokenRefresh();
    if (result) {
      setAccessToken(result.access_token);
      setRefreshToken(result.refresh_token);
      scheduleTokenRefresh();
    }
    return result;
  } catch (err) {
    if (isAuthExpiredError(err)) {
      notifyAuthExpired((err as AxiosError<{ code?: string }>).response?.data?.code);
    }
    throw err;
  }
};

// 다른 탭에서 토큰 갱신/로그아웃 시 동기화
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "access_token" && e.newValue) {
      accessToken = e.newValue;
      authExpiredNotified = false;
      scheduleTokenRefresh();
    }
    if (e.key === "auth-storage" && !e.newValue) {
      // 다른 탭에서 로그아웃/세션 만료
      accessToken = null;
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
  });
}

// Proactive token refresh - 만료 5분 전 자동 갱신
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
const REFRESH_RETRY_DELAY_MS = 30000;

const scheduleTokenRefresh = () => {
  if (refreshTimer) clearTimeout(refreshTimer);

  const token = getAccessToken();
  if (!token) return;

  const expiry = getTokenExpiry(token);
  if (!expiry) return;

  // 만료 5분 전에 갱신 (최소 10초 후)
  const delay = Math.max(expiry - Date.now() - 5 * 60 * 1000, 10000);

  refreshTimer = setTimeout(() => {
    // 성공 시 refreshSession 이 재스케줄. 일시 실패(네트워크 등)는 30초 뒤 재시도.
    // 인증 만료(401/403)는 refreshSession 이 세션을 정리하므로 여기서는 아무것도 하지 않는다.
    refreshSession().catch(() => {
      if (getAccessToken()) refreshTimer = setTimeout(scheduleTokenRefresh, REFRESH_RETRY_DELAY_MS);
    });
  }, delay);
};

// 탭 복귀 시 토큰 만료 확인 (만료 1분 이내·이미 만료면 즉시 갱신)
if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    const token = getAccessToken();
    if (!token) return;
    const expiry = getTokenExpiry(token);
    if (expiry && expiry - Date.now() < 60000) {
      refreshSession().catch(() => {});
    }
  });

  // 초기 스케줄링
  setTimeout(() => scheduleTokenRefresh(), 1000);
}

// ─── 응답 인터셉터 ───────────────────────────────────────────────────────────
// 401 이면 refreshSession 을 기다렸다가 재시도한다. 동시에 들어온 401 은 모두 같은
// 갱신 Promise 를 기다리므로 별도 큐가 없다 (과거 큐는 타이머/탭복귀 갱신과 겹치면
// 영원히 풀리지 않아 화면이 무한 로딩되는 원인이었다).
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;
    const errorCode = (error.response?.data as { code?: string } | undefined)?.code;

    // 로그인/기업확인 요청의 401은 LoginPage에서 직접 처리 (인터셉터 건너뜀)
    const url = originalRequest?.url ?? "";
    const isAuthRequest = url.includes("/auth/login") || url.includes("/auth/check-companies");
    if (isAuthRequest) {
      return Promise.reject(error);
    }

    // 차단된 기기 / 비밀번호 변경 → 갱신 시도 없이 즉시 세션 종료
    if (
      status === 401 &&
      (errorCode === "DEVICE_BLOCKED" || errorCode === "DEVICE_NOT_TRUSTED" || errorCode === "PASSWORD_CHANGED")
    ) {
      notifyAuthExpired(errorCode);
      return Promise.reject(error);
    }

    // 구독 잠금 → 잠금 화면으로 (read_only는 호출부 onError 토스트로 처리)
    // 단, 토큰이 아직 localStorage에 저장되기 전(로그인 처리 중: authStore.login 3단계)에는
    // 강제 새로고침하지 않는다 — 새로고침하면 메모리의 토큰이 사라져 미인증으로 /login에 떨어지고
    // "로그인이 안 된다"로 보인다. 이 경우 LoginPage가 로그인 완료 후 /billing/locked로 보낸다.
    if (status === 403 && errorCode === "SUBSCRIPTION_LOCKED") {
      if (
        typeof window !== "undefined" &&
        getAccessToken() &&
        !window.location.pathname.startsWith("/billing/locked")
      ) {
        window.location.href = "/billing/locked";
      }
      return Promise.reject(error);
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // 이미 다른 경로(다른 탭·타이머)가 토큰을 교체했다면 갱신 없이 새 토큰으로 재시도
      const usedAuth = originalRequest.headers?.Authorization;
      const current = getAccessToken();
      if (current && typeof usedAuth === "string" && usedAuth !== `Bearer ${current}`) {
        originalRequest.headers.Authorization = `Bearer ${current}`;
        return api(originalRequest);
      }

      if (!getRefreshToken()) {
        notifyAuthExpired();
        return Promise.reject(error);
      }

      try {
        const result = await refreshSession();
        if (!result) return Promise.reject(error);
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${result.access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // 인증 만료는 refreshSession 이 이미 정리했다. 그 외(네트워크·5xx)는 토큰 유지.
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// File URL helper for MinIO files
export const getFileUrl = (filePath: string | null | undefined): string => {
  if (!filePath) return "";

  // If it's already a full URL, return as is
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }

  // Get base URL without /api/v1
  const hostname = typeof window !== "undefined" ? window.location.hostname : "localhost";
  const protocol = typeof window !== "undefined" ? window.location.protocol : "http:";

  // External domain access - use /storage/ionemngr/ (nginx proxy to MinIO)
  if (hostname === "ione.dev-one.co.kr" || hostname === "i1.dev-one.co.kr") {
    return `${protocol}//${hostname}/storage/ionemngr/${filePath}`;
  }

  // Use Vite proxy for all other cases
  // Vite dev server will proxy /storage/ionemngr -> http://minio:9000/ionemngr
  return `/storage/ionemngr/${filePath}`;
};

// Profile image URL helper - uses API endpoint
// Cache buster changes every minute to balance between caching and freshness
export const getProfileImageUrl = (
  userId: string | null | undefined,
  size: "thumb" | "original" = "thumb",
): string => {
  if (!userId) return "";

  const hostname = typeof window !== "undefined" ? window.location.hostname : "localhost";
  const protocol = typeof window !== "undefined" ? window.location.protocol : "http:";

  // Cache buster - changes every minute
  const cacheBuster = Math.floor(Date.now() / 60000);
  const sizeParam = size === "thumb" ? "&size=thumb" : "";

  // External domain access (use same domain for CORS compatibility)
  if (hostname === "ione.dev-one.co.kr" || hostname === "i1.dev-one.co.kr") {
    return `${protocol}//${hostname}/api/v1/auth/profile-image/${userId}?v=${cacheBuster}${sizeParam}`;
  }

  // Use Vite proxy for all other cases
  return `/api/v1/auth/profile-image/${userId}?v=${cacheBuster}${sizeParam}`;
};

// API error helper
export interface ApiError {
  message: string;
  detail?: string;
  status?: number;
  code?: string;
}

export const getApiError = (error: unknown): ApiError => {
  console.log('🔍 [getApiError] Raw error:', error);

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      error?: string;      // 백엔드가 'error' 필드로 보냄
      detail?: string;
      message?: string;    // 일부 엔드포인트는 'message' 사용
      code?: string;
    }>;

    console.log('🔍 [getApiError] Axios error response:', axiosError.response?.data);

    return {
      message:
        axiosError.response?.data?.error ||      // 우선순위 1: error 필드
        axiosError.response?.data?.message ||    // 우선순위 2: message 필드
        axiosError.response?.data?.detail ||     // 우선순위 3: detail 필드
        axiosError.message ||
        "알 수 없는 오류가 발생했습니다.",
      code: axiosError.response?.data?.code,
      status: axiosError.response?.status,
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: "알 수 없는 오류가 발생했습니다." };
};

export default api;
